export const ethicsInFinanceCourse = {
  id: "ethics-in-finance-2024",
  title: "Ethics in Finance: Complete Professional Course",
  shortDescription: "Master ethical principles, professional standards, and responsible finance practices for sustainable and trustworthy financial services.",
  description: "Comprehensive course covering financial ethics, professional conduct, fiduciary duty, ESG principles, and ethical decision-making frameworks in finance.",
  category: "Finance",
  industryCategory: "Ethics and Compliance",
  difficulty: "intermediate",
  duration: 14,
  estimatedWeeks: 7,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from ethics professionals and compliance experts",
  thumbnailUrl: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand fundamental ethical principles in finance",
    "Apply professional codes of conduct and standards",
    "Navigate conflicts of interest and fiduciary duties",
    "Implement ESG and sustainable finance principles",
    "Analyze ethical dilemmas using decision frameworks",
    "Understand regulatory and compliance requirements",
    "Apply stakeholder theory and corporate responsibility",
    "Develop ethical leadership and culture in finance"
  ],

  prerequisites: [
    "Basic Finance Knowledge",
    "Business Ethics Fundamentals"
  ],

  skills: [
    "Ethical Analysis",
    "Compliance Management",
    "Stakeholder Assessment",
    "Fiduciary Responsibility",
    "ESG Integration",
    "Risk Assessment",
    "Professional Standards",
    "Ethical Leadership"
  ],

  marketData: {
    averageSalary: 85000,
    salaryRange: {
      min: 60000,
      max: 130000
    },
    jobDemand: 4.0,
    marketTrend: "growing",
    careerPaths: [
      "Compliance Officer",
      "Ethics Officer", 
      "Risk Manager",
      "ESG Analyst",
      "Financial Advisor",
      "Internal Auditor"
    ],
    relatedSkills: [
      "Regulatory Knowledge",
      "Risk Management",
      "Policy Development",
      "Training and Education",
      "Investigation Skills",
      "Communication"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "ethical-foundations",
      title: "Ethical Foundations in Finance",
      description: "Fundamental ethical principles and moral frameworks for finance",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "ethics-introduction",
          title: "Introduction to Financial Ethics",
          description: "Why ethics matter in finance and the cost of unethical behavior",
          type: "video",
          duration: 30
        },
        {
          id: "ethical-frameworks",
          title: "Ethical Frameworks and Moral Philosophy",
          description: "Applying ethical theories to financial decision making",
          type: "text",
          duration: 50,
          textContent: `# Ethical Frameworks and Moral Philosophy

## Foundations of Ethics in Finance

### Why Ethics Matter in Finance

**Trust as Foundation**
- Financial markets depend on trust and confidence
- Information asymmetries require ethical behavior
- Long-term sustainability requires ethical practices
- Reputation and credibility as competitive advantages

**Costs of Unethical Behavior**
- **Individual Level**: Career destruction, legal consequences, personal guilt
- **Firm Level**: Reputation damage, regulatory sanctions, financial losses
- **Market Level**: Reduced confidence, increased regulation, systemic risk
- **Society Level**: Economic instability, inequality, loss of faith in institutions

**Historical Examples of Ethical Failures**
- Enron: Complex transactions to hide debt and inflate profits
- Bernie Madoff: Ponzi scheme defrauding investors of $65 billion
- Wells Fargo: Fake accounts scandal damaging customer trust
- LIBOR manipulation: Banks manipulating benchmark interest rates

### Moral Philosophy and Ethical Theories

**Consequentialism/Utilitarianism**
- **Principle**: Actions judged by their consequences
- **Utility Maximization**: Greatest good for greatest number
- **Application**: Cost-benefit analysis of ethical decisions
- **Challenges**: Difficult to predict all consequences, measurement issues

**Deontological Ethics**
- **Principle**: Actions have inherent rightness or wrongness
- **Duty-Based**: Focus on moral duties and rules
- **Categorical Imperative**: Universal principles that apply to all
- **Application**: Professional codes of conduct, fiduciary duties

**Virtue Ethics**
- **Principle**: Focus on character traits and moral virtues
- **Character Development**: Building moral habits and dispositions
- **Role Models**: Learning from exemplars of virtue
- **Application**: Professional development, leadership training

**Care Ethics**
- **Principle**: Emphasis on relationships and caring
- **Context Sensitivity**: Attention to particular situations
- **Responsibility**: Care for vulnerable parties
- **Application**: Client relationships, stakeholder consideration

## Professional Ethical Standards

### Fiduciary Duty

**Definition and Components**
- **Duty of Care**: Acting with competence and diligence
- **Duty of Loyalty**: Putting client interests first
- **Duty of Good Faith**: Honest and fair dealing
- **Duty of Disclosure**: Transparent communication

**Applications in Finance**
- **Investment Advisors**: Portfolio management and advice
- **Corporate Directors**: Shareholder and stakeholder interests
- **Trustees**: Beneficiary protection and management
- **Financial Planners**: Comprehensive client service

**Conflicts with Fiduciary Duty**
- Commission-based compensation vs. client interests
- Proprietary products vs. best available options
- Short-term profits vs. long-term client welfare
- Personal interests vs. professional obligations

### Professional Codes of Conduct

**CFA Institute Code of Ethics**
- Act with integrity, competence, diligence, and respect
- Place integrity of profession above personal interests
- Use reasonable care and exercise independent judgment
- Practice and encourage others to practice professionally and ethically

**CFA Standards of Professional Conduct**
- **Professionalism**: Knowledge of law, independence and objectivity
- **Integrity of Capital Markets**: Material nonpublic information, market manipulation
- **Duties to Clients**: Loyalty, prudence, fair dealing, confidentiality
- **Duties to Employers**: Loyalty, additional compensation, responsibilities of supervisors
- **Investment Analysis**: Diligence, communication, record retention
- **Conflicts of Interest**: Disclosure, priority of transactions, referral fees
- **Responsibilities as Member**: Conduct, reference to designation

**Other Professional Standards**
- **CFP Board Standards**: Fiduciary duty for financial planners
- **FPA Code of Ethics**: Financial Planning Association standards
- **AICPA Standards**: Accounting and auditing ethics
- **Securities Industry Standards**: FINRA rules and regulations

## Stakeholder Theory and Responsibilities

### Stakeholder Identification and Analysis

**Primary Stakeholders**
- **Shareholders**: Owners seeking returns on investment
- **Employees**: Workers depending on fair treatment and job security
- **Customers**: Service recipients expecting fair treatment
- **Creditors**: Lenders requiring repayment and transparency

**Secondary Stakeholders**
- **Communities**: Local areas affected by business operations
- **Suppliers**: Business partners in value chain
- **Government**: Regulatory and tax authorities
- **Society**: Broader public interest and welfare

**Stakeholder Analysis Framework**
1. **Identification**: Who are the stakeholders?
2. **Interests**: What do they care about?
3. **Power**: How much influence do they have?
4. **Legitimacy**: What claims do they have?
5. **Urgency**: How pressing are their concerns?

### Balancing Stakeholder Interests

**Stakeholder Capitalism Model**
- Consider interests of all stakeholders, not just shareholders
- Long-term value creation for all parties
- Sustainable business practices
- Social license to operate

**Shareholder Primacy vs. Stakeholder Theory**
- **Shareholder Primacy**: Maximize shareholder value
- **Stakeholder Theory**: Balance multiple stakeholder interests
- **Practical Challenges**: Conflicts between stakeholder groups
- **Resolution Strategies**: Communication, compromise, long-term thinking

## Ethical Decision-Making Frameworks

### Systematic Ethical Analysis

**Identify the Ethical Issue**
- What ethical principles are at stake?
- Who are the affected parties?
- What are the potential consequences?
- Are there conflicts of interest?

**Gather Information**
- What are the facts?
- What are the uncertainties?
- What laws and regulations apply?
- What professional standards are relevant?

**Consider Alternative Actions**
- What options are available?
- What are the consequences of each option?
- Which stakeholders are affected by each option?
- What are the ethical implications?

**Apply Ethical Tests**
- **Consequences Test**: What are the likely outcomes?
- **Duties Test**: What are my professional obligations?
- **Character Test**: What would a person of integrity do?
- **Publicity Test**: Would I be comfortable if this were public?

### Practical Decision Tools

**Ethics Check Questions**
1. Is it legal?
2. Is it ethical according to professional standards?
3. How would I feel if this were published in the newspaper?
4. Would I want to be treated this way?
5. Does this pass the "sleep test" - can I sleep well at night?

**Stakeholder Impact Analysis**
- Who benefits from this decision?
- Who is harmed by this decision?
- Are the benefits and harms fairly distributed?
- Are there ways to minimize harm while achieving benefits?

## Cultural and Organizational Ethics

### Ethical Culture Development

**Tone at the Top**
- Leadership commitment to ethical behavior
- Consistent messaging about ethics importance
- Accountability for ethical failures
- Recognition and reward for ethical behavior

**Organizational Structure**
- Clear reporting lines for ethical concerns
- Ethics officer or ombudsman role
- Ethics committees and oversight
- Integration of ethics into business processes

**Training and Communication**
- Regular ethics training programs
- Case study discussions and scenarios
- Clear communication of expectations
- Updates on regulatory changes

### Ethics Infrastructure

**Policies and Procedures**
- Code of conduct and ethics policies
- Conflict of interest policies
- Whistleblower protection procedures
- Investigation and disciplinary processes

**Monitoring and Assessment**
- Regular ethics assessments and surveys
- Monitoring of key risk indicators
- Audit of ethics compliance
- Benchmarking against industry standards

**Reporting and Response**
- Multiple channels for reporting concerns
- Confidential and anonymous options
- Prompt investigation and response
- Communication of actions taken

## Global and Cultural Considerations

### Cross-Cultural Ethics

**Cultural Relativism vs. Universal Standards**
- Different cultural norms and values
- Universal human rights and principles
- Professional standards across cultures
- Balancing respect for culture with ethical standards

**International Business Ethics**
- Corruption and bribery in different cultures
- Gift-giving customs and business practices
- Different regulatory environments
- Multinational corporation responsibilities

### Regulatory Harmonization

**International Standards**
- Basel Accords for banking regulation
- IOSCO principles for securities regulation
- FATF standards for anti-money laundering
- OECD guidelines for multinational enterprises

**Challenges in Implementation**
- Different legal systems and enforcement
- Varying cultural attitudes toward regulation
- Competitive disadvantages from strict standards
- Coordination across jurisdictions

## Contemporary Ethical Challenges

### Technology and Digital Ethics

**Algorithmic Bias and Fairness**
- Machine learning models in credit decisions
- Bias in automated trading systems
- Transparency in algorithm decision-making
- Accountability for algorithmic outcomes

**Data Privacy and Security**
- Customer data protection and privacy
- Cybersecurity and data breach response
- Cross-border data transfer issues
- Consent and control over personal data

### Sustainable Finance and ESG

**Environmental Responsibility**
- Climate change and carbon footprint
- Natural resource conservation
- Pollution and environmental impact
- Green finance and sustainable investing

**Social Responsibility**
- Labor practices and human rights
- Diversity and inclusion initiatives
- Community investment and development
- Product safety and consumer protection

**Governance Excellence**
- Board diversity and independence
- Executive compensation and accountability
- Transparency and disclosure
- Stakeholder engagement and dialogue

Ethics in finance requires both principled thinking and practical application, balancing multiple stakeholder interests while maintaining professional integrity and contributing to societal well-being.`
        },
        {
          id: "ethical-dilemma-simulator",
          title: "Ethical Dilemma Analysis Workshop",
          description: "Practice analyzing and resolving ethical dilemmas in finance",
          type: "finance-interactive",
          duration: 40,
          interactiveData: {
            exerciseType: "ethical-analysis",
            instructions: "Analyze ethical dilemmas using systematic frameworks and professional standards to reach well-reasoned decisions.",
            scenario: {
              dilemmas: [
                {
                  id: "dilemma1",
                  title: "Conflict of Interest",
                  description: "A financial advisor discovers their firm receives higher commissions for recommending certain investment products that may not be optimal for clients.",
                  stakeholders: ["Clients", "Advisor", "Firm", "Product Providers"],
                  considerations: ["Fiduciary Duty", "Disclosure", "Alternative Solutions", "Professional Standards"]
                },
                {
                  id: "dilemma2", 
                  title: "Insider Information",
                  description: "An analyst learns material non-public information about a company during a client meeting that could affect investment decisions.",
                  stakeholders: ["Investors", "Company", "Analyst", "Market Integrity"],
                  considerations: ["Legal Requirements", "Market Fairness", "Confidentiality", "Professional Responsibility"]
                }
              ]
            },
            hints: [
              "Consider all affected stakeholders",
              "Apply multiple ethical frameworks",
              "Think about short-term vs long-term consequences",
              "Consider professional standards and regulations"
            ]
          }
        }
      ],
      quiz: {
        id: "ethical-foundations-quiz",
        title: "Ethical Foundations Quiz",
        description: "Test your understanding of ethical principles in finance",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary principle of fiduciary duty?",
            options: [
              "Maximizing personal profits",
              "Following company policies",
              "Putting client interests first",
              "Avoiding all risks"
            ],
            correctAnswer: 2,
            explanation: "Fiduciary duty requires putting client interests ahead of one's own interests, acting with loyalty, care, and good faith."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "According to stakeholder theory, businesses should consider:",
            options: [
              "Only shareholder interests",
              "Only customer interests", 
              "All affected stakeholder interests",
              "Only regulatory requirements"
            ],
            correctAnswer: 2,
            explanation: "Stakeholder theory suggests that businesses should consider the interests of all stakeholders affected by their decisions, not just shareholders."
          }
        ]
      }
    },

    {
      id: "regulatory-compliance",
      title: "Regulatory Framework and Compliance",
      description: "Understanding regulatory requirements and compliance obligations",
      order: 2,
      estimatedHours: 3.5,
      content: [
        {
          id: "regulatory-overview",
          title: "Financial Regulation Overview",
          description: "Key regulatory bodies and compliance requirements",
          type: "video",
          duration: 35
        },
        {
          id: "compliance-frameworks",
          title: "Compliance Frameworks and Implementation",
          description: "Building effective compliance programs and risk management",
          type: "text", 
          duration: 45,
          textContent: `# Compliance Frameworks and Implementation

## Regulatory Landscape

### Key Regulatory Bodies

**Securities and Exchange Commission (SEC)**
- **Mission**: Protect investors, maintain fair markets, facilitate capital formation
- **Jurisdiction**: Securities markets, investment advisors, public companies
- **Key Regulations**: Securities Act of 1933, Securities Exchange Act of 1934
- **Enforcement**: Investigation and prosecution of violations

**Financial Industry Regulatory Authority (FINRA)**
- **Role**: Self-regulatory organization for broker-dealers
- **Functions**: Licensing, examination, enforcement
- **Rules**: Conduct rules, operational requirements, disclosure obligations
- **BrokerCheck**: Public database of broker information

**Commodity Futures Trading Commission (CFTC)**
- **Oversight**: Derivatives markets, futures, swaps
- **Mission**: Market integrity, customer protection, systemic risk oversight
- **Regulations**: Dodd-Frank Act implementation, position limits
- **Registration**: Futures commission merchants, commodity pool operators

**Other Key Regulators**
- **Federal Reserve**: Banking supervision, monetary policy
- **Office of the Comptroller of the Currency (OCC)**: National bank supervision
- **Consumer Financial Protection Bureau (CFPB)**: Consumer protection
- **State Securities Regulators**: State-level investment advisor oversight

### Regulatory Evolution

**Response to Financial Crises**
- **1929 Stock Market Crash**: Securities Acts of 1933 and 1934
- **Savings and Loan Crisis**: FIRREA and enhanced supervision
- **2008 Financial Crisis**: Dodd-Frank Act comprehensive reform
- **Recent Developments**: COVID-19 response, cryptocurrency regulation

**Trends in Regulation**
- **Principles-Based vs. Rules-Based**: Flexibility vs. specificity
- **Risk-Based Supervision**: Focus on highest-risk activities
- **Technology Integration**: RegTech and automated compliance
- **Global Coordination**: International regulatory harmonization

## Compliance Program Elements

### Program Structure

**Three Lines of Defense**
1. **First Line**: Business units owning and managing risks
2. **Second Line**: Risk management and compliance oversight
3. **Third Line**: Internal audit providing independent assurance

**Governance Structure**
- **Board Oversight**: Ultimate responsibility for compliance culture
- **Chief Compliance Officer**: Senior executive accountability
- **Compliance Committee**: Cross-functional coordination
- **Business Unit Responsibility**: Operational compliance ownership

### Risk Assessment and Management

**Compliance Risk Identification**
- **Regulatory Mapping**: Understanding applicable requirements
- **Business Activity Analysis**: Identifying compliance touchpoints
- **Risk Register**: Cataloging and prioritizing risks
- **Scenario Analysis**: Considering potential compliance failures

**Risk Assessment Process**
1. **Inherent Risk**: Risk before controls
2. **Control Assessment**: Evaluating existing controls
3. **Residual Risk**: Risk after controls
4. **Risk Treatment**: Accept, mitigate, transfer, or avoid
5. **Monitoring**: Ongoing assessment and adjustment

### Policies and Procedures

**Policy Framework**
- **Code of Conduct**: Overarching ethical principles
- **Specific Policies**: Detailed rules for particular areas
- **Procedures**: Step-by-step implementation guidance
- **Guidelines**: Best practice recommendations

**Policy Development Process**
1. **Regulatory Analysis**: Understanding requirements
2. **Risk Assessment**: Identifying policy needs
3. **Stakeholder Input**: Business and legal review
4. **Approval Process**: Management and board approval
5. **Implementation**: Training and communication
6. **Review and Update**: Regular policy maintenance

## Anti-Money Laundering (AML)

### AML Framework

**Bank Secrecy Act (BSA)**
- **Currency Transaction Reports (CTRs)**: Transactions over $10,000
- **Suspicious Activity Reports (SARs)**: Unusual or suspicious activity
- **Customer Identification Program (CIP)**: Know your customer requirements
- **Record Keeping**: Transaction and customer records

**USA PATRIOT Act**
- **Enhanced Due Diligence**: High-risk customers and accounts
- **Beneficial Ownership**: Identifying ultimate beneficial owners
- **Correspondent Banking**: Enhanced controls for foreign banks
- **Information Sharing**: Coordination with law enforcement

### AML Program Components

**Customer Due Diligence (CDD)**
- **Customer Identification**: Verifying customer identity
- **Beneficial Ownership**: Understanding ownership structures
- **Risk Assessment**: Evaluating customer risk profiles
- **Ongoing Monitoring**: Continuous assessment of activity

**Suspicious Activity Monitoring**
- **Transaction Monitoring**: Automated systems and alerts
- **Investigation Process**: Analysis of potentially suspicious activity
- **SAR Filing**: Reporting to FinCEN when required
- **Law Enforcement Cooperation**: Supporting investigations

## Investment Advisor Regulation

### Registration Requirements

**Federal vs. State Registration**
- **Federal**: Advisors with $100+ million in assets under management
- **State**: Smaller advisors below federal thresholds
- **Exempt**: Private fund advisors, insurance company advisors
- **Notice Filing**: State notice requirements for federal advisors

**Form ADV**
- **Part 1**: Registration and business information
- **Part 2**: Brochure with services and fees
- **Annual Updates**: Maintaining current information
- **Public Availability**: Client access to advisor information

### Fiduciary Duties and Standards

**Investment Advisers Act of 1940**
- **Fiduciary Standard**: Duty of care and loyalty
- **Best Interest**: Client interests over advisor interests
- **Disclosure**: Material conflicts of interest
- **Reasonable Basis**: Due diligence in recommendations

**Custody Rules**
- **Qualified Custodian**: Independent custody arrangements
- **Surprise Examinations**: Annual audits for custody
- **Account Statements**: Direct delivery to clients
- **Standing Letters**: Authorization for third-party transfers

## Market Conduct and Ethics

### Insider Trading

**Material Non-Public Information**
- **Materiality**: Information affecting investment decisions
- **Non-Public**: Not available to general public
- **Trading Prohibition**: Using or sharing inside information
- **Tipping**: Providing information to others

**Compliance Measures**
- **Information Barriers**: Chinese walls between departments
- **Watch Lists**: Monitoring restricted securities
- **Pre-clearance**: Approval for employee trading
- **Training**: Education on insider trading rules

### Market Manipulation

**Prohibited Practices**
- **Pump and Dump**: Artificially inflating prices
- **Wash Trading**: Transactions without change of ownership
- **Marking the Close**: Manipulating closing prices
- **Spoofing**: False orders to influence prices

**Detection and Prevention**
- **Market Surveillance**: Monitoring trading patterns
- **Automated Alerts**: Technology-based detection systems
- **Investigation Procedures**: Response to suspicious activity
- **Regulatory Reporting**: Coordination with regulators

## Technology and Cyber Compliance

### Cybersecurity Frameworks

**NIST Cybersecurity Framework**
- **Identify**: Asset management and risk assessment
- **Protect**: Access controls and protective technology
- **Detect**: Continuous monitoring and detection processes
- **Respond**: Incident response and communication
- **Recover**: Recovery planning and improvements

**Regulatory Requirements**
- **SEC Cybersecurity Rules**: Disclosure and risk management
- **FINRA Guidelines**: Cybersecurity practices for broker-dealers
- **State Notification**: Data breach notification requirements
- **International Standards**: GDPR, ISO 27001 compliance

### Data Protection and Privacy

**Customer Privacy**
- **Gramm-Leach-Bliley Act**: Financial privacy requirements
- **CCPA/CPRA**: California consumer privacy rights
- **GDPR**: European data protection regulation
- **Consent Management**: Customer authorization and control

**Data Governance**
- **Data Classification**: Sensitivity levels and handling
- **Access Controls**: Role-based access to information
- **Retention Policies**: Data lifecycle management
- **Third-Party Risk**: Vendor due diligence and oversight

## Global Compliance Considerations

### Cross-Border Regulation

**Extraterritorial Application**
- **U.S. Securities Laws**: Global reach of U.S. regulations
- **MiFID II**: European investment services regulation
- **Basel Accords**: International banking standards
- **FATCA**: Foreign account tax compliance

**Regulatory Coordination**
- **Mutual Recognition**: Accepting foreign regulatory oversight
- **Information Sharing**: Cross-border cooperation agreements
- **Harmonization**: Aligning regulatory standards
- **Passporting**: Operating across jurisdictions

### Cultural and Legal Differences

**Legal Systems**
- **Common Law**: Precedent-based legal systems
- **Civil Law**: Code-based legal systems
- **Religious Law**: Sharia-compliant finance
- **Mixed Systems**: Combination of legal traditions

**Business Practices**
- **Gift and Entertainment**: Cultural norms vs. bribery rules
- **Relationship Building**: Personal vs. professional boundaries
- **Decision Making**: Individual vs. consensus approaches
- **Communication Styles**: Direct vs. indirect communication

## Enforcement and Sanctions

### Regulatory Enforcement

**Types of Sanctions**
- **Monetary Penalties**: Fines and disgorgement
- **Cease and Desist Orders**: Stopping violative conduct
- **Suspensions**: Temporary prohibition from activities
- **Bars**: Permanent prohibition from industry

**Enforcement Process**
1. **Investigation**: Gathering evidence and information
2. **Wells Notice**: Opportunity to respond before action
3. **Settlement Negotiations**: Resolving without admission
4. **Administrative Proceedings**: Formal hearing process
5. **Appeal Process**: Reviewing enforcement decisions

### Best Practices for Compliance

**Proactive Compliance Culture**
- **Senior Management Commitment**: Tone at the top
- **Employee Training**: Regular education and updates
- **Clear Expectations**: Specific compliance requirements
- **Accountability**: Consequences for violations

**Continuous Improvement**
- **Regular Assessments**: Testing program effectiveness
- **Benchmarking**: Comparing to industry practices
- **Lessons Learned**: Learning from violations
- **Innovation**: Adopting new compliance technologies

Effective compliance requires understanding the regulatory landscape, implementing comprehensive programs, and maintaining a culture of ethical behavior and legal compliance.`
        }
      ],
      quiz: {
        id: "regulatory-compliance-quiz",
        title: "Regulatory Framework and Compliance Quiz",
        description: "Test your knowledge of regulatory requirements",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary purpose of a Suspicious Activity Report (SAR)?",
            options: [
              "To report all transactions over $10,000",
              "To report potentially suspicious or unusual activity",
              "To report all international wire transfers",
              "To report customer complaints"
            ],
            correctAnswer: 1,
            explanation: "SARs are filed to report potentially suspicious or unusual activity that may indicate money laundering, fraud, or other illegal activities."
          },
          {
            id: "q2",
            type: "multiple-choice", 
            question: "What is the 'three lines of defense' model in compliance?",
            options: [
              "Business units, compliance, and regulators",
              "Prevention, detection, and response",
              "Business units, risk management/compliance, and internal audit",
              "Policies, procedures, and training"
            ],
            correctAnswer: 2,
            explanation: "The three lines of defense model consists of: (1) business units owning and managing risks, (2) risk management and compliance providing oversight, and (3) internal audit providing independent assurance."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "ethics-case-analysis",
      title: "Financial Ethics Case Study Analysis",
      description: "Analyze a complex ethical dilemma in finance and develop recommendations using ethical frameworks",
      type: "project",
      dueWeeks: 4,
      maxScore: 100,
      instructions: "Comprehensive ethical analysis applying multiple frameworks to resolve complex financial ethics dilemmas.",
      submissionFormat: "pdf",
      resources: [
        "Ethical frameworks and decision tools",
        "Professional codes of conduct",
        "Regulatory guidance and case studies"
      ]
    }
  ],

  finalExam: {
    id: "ethics-in-finance-final",
    title: "Ethics in Finance Final Exam", 
    description: "Comprehensive examination of financial ethics principles and applications",
    timeLimit: 120,
    passingScore: 80,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "What is the fundamental principle underlying fiduciary duty?",
        options: [
          "Maximizing profits for the firm",
          "Following regulatory requirements",
          "Putting client interests ahead of one's own",
          "Avoiding all conflicts of interest"
        ],
        correctAnswer: 2,
        explanation: "Fiduciary duty fundamentally requires putting the client's interests ahead of one's own interests, acting with loyalty, care, and good faith."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "According to the CFA Institute Code of Ethics, what should members do regarding the integrity of the profession?",
        options: [
          "Focus only on personal reputation",
          "Place integrity of profession above personal interests",
          "Prioritize client relationships over profession",
          "Avoid involvement in professional activities"
        ],
        correctAnswer: 1,
        explanation: "The CFA Code of Ethics requires members to place the integrity of the investment profession and the interests of clients above their own personal interests."
      }
    ]
  },

  quizzes: []
};